import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import EmptyState from '../shared/EmptyState';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import { url } from '../../services/utils';
import theme from '../../data/theme';
import { Maps } from '../Maps';
import { getAddressMap } from '../../api/getMapAddress';

// Memoized Map Section to completely prevent Leaflet re-renders when user types in form fields
const AddressMapSection = React.memo(
    ({ latitude, longitude, mapSearchText, onMapClick, onSetLat, onSetLng }) => {
        const [isMapReady, setIsMapReady] = useState(false);

        useEffect(() => {
            // Defer mounting heavy Leaflet map so the modal opens instantaneously with zero lag
            const timer = setTimeout(() => {
                setIsMapReady(true);
            }, 50);
            return () => clearTimeout(timer);
        }, []);

        return (
            <div className="modal-section-card">
                <div className="modal-section-title">
                    <i className="fas fa-map-marker-alt" />
                    <span>Titik Lokasi Pengiriman (Peta)</span>
                </div>

                <div className="modal-map-frame">
                    {isMapReady ? (
                        <Maps
                            defaultCenter={{
                                lat: latitude,
                                lng: longitude,
                            }}
                            defaultZoom={15}
                            onClickMap={onMapClick}
                            height="240px"
                            isSearch={true}
                            long={longitude}
                            lat={latitude}
                            address={mapSearchText}
                            setLong={onSetLng}
                            setLat={onSetLat}
                        />
                    ) : (
                        <div
                            style={{
                                height: '240px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#f8fafc',
                                borderRadius: '10px',
                                border: '1px solid #e2e8f0',
                                color: '#64748b',
                                fontSize: '0.88rem',
                                gap: '8px',
                            }}
                        >
                            <i className="fas fa-spinner fa-spin" style={{ color: '#0064d2', fontSize: '1.4rem' }} />
                            <span>Menyiapkan peta...</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

const AccountPageAddresses = () => {
    const [addressData, setAddress] = useState([]);
    const [loading, setLoading] = useState(true);
    const [defaultAddressId, setDefaultAddressId] = useState(null);
    const [settingDefaultId, setSettingDefaultId] = useState(null);

    // Modal state for Add / Edit
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
    const [editAddressId, setEditAddressId] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Decoupled map search string to prevent laggy typing
    const [mapSearchText, setMapSearchText] = useState('');

    // Form fields
    const [formData, setFormData] = useState({
        address: '',
        province: '',
        city: '',
        district: '',
        village: '',
        phone: '',
        latitude: -7.3106144,
        longitude: 112.6841525,
        isDefault: false,
    });

    // Cascading dropdown lists
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [villages, setVillages] = useState([]);

    // Location cache to prevent slow repeated API requests
    const locationCache = useRef({
        cities: {},
        districts: {},
        villages: {},
    });

    // Delete confirmation modal state
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        address: null,
        loading: false,
    });

    const { token } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const history = useHistory();

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isModalOpen || deleteModal.isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isModalOpen, deleteModal.isOpen]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isModalOpen && !saving) setIsModalOpen(false);
                if (deleteModal.isOpen && !deleteModal.loading) {
                    setDeleteModal({ isOpen: false, address: null, loading: false });
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, deleteModal.isOpen, saving]);

    // Fetch list of addresses
    const fetchAddresses = useCallback(async (isSilent = false) => {
        if (!token) return;
        try {
            if (!isSilent) {
                setLoading(true);
            }
            const [addressRes, userRes] = await Promise.allSettled([
                axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/address/list`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }),
                axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/auth/info`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }),
            ]);

            const list = addressRes.status === 'fulfilled' ? addressRes.value.data?.response || [] : [];
            const safeList = Array.isArray(list) ? list : [];
            setAddress(safeList);

            const userInfo = userRes.status === 'fulfilled' ? userRes.value.data?.response : null;
            if (userInfo) {
                localStorage.setItem('user_profile_data', JSON.stringify(userInfo));
            }
            const userProfileAddrId = userInfo?.address_id;
            const savedLocalDefault = localStorage.getItem('DEFAULT_ADDRESS_ID');

            let determinedDefaultId = null;

            // 1. Prioritize saved choice from localStorage if it exists in the address list
            if (
                savedLocalDefault &&
                safeList.some((a) => String(a.address_id ?? a.id) === String(savedLocalDefault))
            ) {
                determinedDefaultId = savedLocalDefault;
            } else {
                // 2. Check if any address item explicitly specifies is_default: 0 or '0' (Hokky backend standard)
                const defZeroItem = safeList.find((a) => a.is_default === 0 || a.is_default === '0');
                if (defZeroItem) {
                    determinedDefaultId = defZeroItem.address_id ?? defZeroItem.id;
                } else if (
                    userProfileAddrId &&
                    safeList.some((a) => String(a.address_id ?? a.id) === String(userProfileAddrId))
                ) {
                    // 3. Check backend user profile address_id
                    determinedDefaultId = userProfileAddrId;
                } else {
                    // 4. Check is_default: 1 / true / default / is_primary
                    const defOneItem = safeList.find(
                        (a) => a.is_default === 1 || a.is_default === true || a.default === 1 || a.is_primary === 1
                    );
                    if (defOneItem) {
                        determinedDefaultId = defOneItem.address_id ?? defOneItem.id;
                    } else if (safeList.length > 0) {
                        determinedDefaultId = safeList[0].address_id ?? safeList[0].id;
                    }
                }
            }

            if (determinedDefaultId != null) {
                setDefaultAddressId(determinedDefaultId);
                localStorage.setItem('DEFAULT_ADDRESS_ID', String(determinedDefaultId));
                localStorage.setItem('CHECKOUT_SELECTED_ADDRESS_ID', String(determinedDefaultId));
                sessionStorage.setItem('CHECKOUT_SELECTED_ADDRESS_ID', String(determinedDefaultId));
            }
        } catch (error) {
            console.error('Error fetching addresses:', error);
            setAddress([]);
        } finally {
            if (!isSilent) {
                setLoading(false);
            }
        }
    }, [token]);

    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    // Fetch Provinces once
    useEffect(() => {
        const fetchProvinces = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_HOST_API}/province/list`);
                setProvinces(res.data?.response || []);
            } catch (err) {
                console.error('Error fetching provinces:', err);
            }
        };
        fetchProvinces();
    }, []);

    // Cascading fetch handlers with caching
    const handleProvinceChange = useCallback(async (provId) => {
        setFormData((prev) => ({
            ...prev,
            province: provId,
            city: '',
            district: '',
            village: '',
        }));
        setCities([]);
        setDistricts([]);
        setVillages([]);
        if (!provId) return;

        if (locationCache.current.cities[provId]) {
            setCities(locationCache.current.cities[provId]);
            return;
        }

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_HOST_API}/city/listing?province_id=${provId}`
            );
            const data = res.data?.response || [];
            locationCache.current.cities[provId] = data;
            setCities(data);
        } catch (err) {
            console.error('Error fetching cities:', err);
        }
    }, []);

    const handleCityChange = useCallback(async (cityId) => {
        setFormData((prev) => ({
            ...prev,
            city: cityId,
            district: '',
            village: '',
        }));
        setDistricts([]);
        setVillages([]);
        if (!cityId) return;

        if (locationCache.current.districts[cityId]) {
            setDistricts(locationCache.current.districts[cityId]);
            return;
        }

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_HOST_API}/district/listing?city_id=${cityId}`
            );
            const data = res.data?.response || [];
            locationCache.current.districts[cityId] = data;
            setDistricts(data);
        } catch (err) {
            console.error('Error fetching districts:', err);
        }
    }, []);

    const handleDistrictChange = useCallback(async (distId) => {
        setFormData((prev) => ({
            ...prev,
            district: distId,
            village: '',
        }));
        setVillages([]);
        if (!distId) return;

        if (locationCache.current.villages[distId]) {
            setVillages(locationCache.current.villages[distId]);
            return;
        }

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_HOST_API}/village/listing?district_id=${distId}`
            );
            const data = res.data?.response || [];
            locationCache.current.villages[distId] = data;
            setVillages(data);
        } catch (err) {
            console.error('Error fetching villages:', err);
        }
    }, []);

    // Set as Default / Utama via POST /address/default/{id}
    const handleSetDefault = async (addressId) => {
        try {
            setSettingDefaultId(addressId);
            dispatch({ type: 'UPDATE_ACTION', payload: true });

            // 1. Optimistically update local state & persistent storages immediately
            setDefaultAddressId(addressId);
            localStorage.setItem('DEFAULT_ADDRESS_ID', String(addressId));
            localStorage.setItem('CHECKOUT_SELECTED_ADDRESS_ID', String(addressId));
            sessionStorage.setItem('CHECKOUT_SELECTED_ADDRESS_ID', String(addressId));

            // Move the selected address card to index 0 immediately so it visually becomes the top card!
            setAddress((prev) => {
                if (!Array.isArray(prev)) return prev;
                const target = prev.find((a) => String(a.address_id ?? a.id) === String(addressId));
                if (!target) return prev;
                const rest = prev.filter((a) => String(a.address_id ?? a.id) !== String(addressId));
                return [target, ...rest];
            });

            // Notify any active components (e.g., checkout page) in real-time
            window.dispatchEvent(new CustomEvent('address:default_updated', { detail: addressId }));

            // 2. Synchronize with backend API
            let synced = false;

            // Attempt A: POST /address/default/{id} without body (Hokky backend standard)
            try {
                await axios({
                    method: 'POST',
                    url: `${process.env.REACT_APP_HOST_API}/address/default/${addressId}`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                synced = true;
            } catch (errA) {
                // Attempt B: POST /default/{id} without body
                try {
                    await axios({
                        method: 'POST',
                        url: `${process.env.REACT_APP_HOST_API}/default/${addressId}`,
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });
                    synced = true;
                } catch (errB) {
                    // Attempt C: POST /address/default/{id} with JSON body
                    try {
                        await axios({
                            method: 'POST',
                            url: `${process.env.REACT_APP_HOST_API}/address/default/${addressId}`,
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                            data: {
                                address_id: addressId,
                                id: addressId,
                            },
                        });
                        synced = true;
                    } catch (errC) {
                        console.warn('Backend default address endpoint notification warning:', errC);
                    }
                }
            }

            toast.success('Alamat berhasil dijadikan sebagai Alamat Utama');
            // Silent refresh from backend without flashing full skeleton
            await fetchAddresses(true);
        } catch (error) {
            console.error('Set Default Error:', error);
            toast.error('Gagal mengatur Alamat Utama');
        } finally {
            setSettingDefaultId(null);
            dispatch({ type: 'UPDATE_ACTION', payload: false });
        }
    };

    // Delete Address via POST /address/delete/{id}
    const confirmDeleteAddress = async () => {
        if (!deleteModal.address) return;
        const targetId = deleteModal.address.address_id;
        try {
            setDeleteModal((prev) => ({ ...prev, loading: true }));
            dispatch({ type: 'UPDATE_ACTION', payload: true });
            await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_HOST_API}/address/delete/${targetId}`,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Alamat berhasil dihapus');
            setDeleteModal({ isOpen: false, address: null, loading: false });
            await fetchAddresses();
        } catch (error) {
            console.error('Delete Address Error:', error.response?.data || error.message);
            toast.error(error.response?.data?.message || 'Gagal menghapus alamat');
            setDeleteModal((prev) => ({ ...prev, loading: false }));
        } finally {
            dispatch({ type: 'UPDATE_ACTION', payload: false });
        }
    };

    // Open Add Modal
    const openAddModal = () => {
        if (addressData.length >= 3) {
            toast.info('Maksimal 3 alamat telah tersimpan. Silakan ubah atau hapus alamat yang ada terlebih dahulu.');
            return;
        }
        setModalMode('add');
        setEditAddressId(null);
        setMapSearchText('');
        setFormData({
            address: '',
            province: '',
            city: '',
            district: '',
            village: '',
            phone: '',
            latitude: -7.3106144,
            longitude: 112.6841525,
            isDefault: addressData.length === 0,
        });
        setCities([]);
        setDistricts([]);
        setVillages([]);
        setIsModalOpen(true);
    };

    // Open Edit Modal
    const openEditModal = async (address) => {
        setModalMode('edit');
        setEditAddressId(address.address_id);
        setIsModalOpen(true);
        setModalLoading(true);

        const currentIsDefault =
            defaultAddressId != null
                ? String(address.address_id) === String(defaultAddressId)
                : Boolean(
                    address.is_default === 1 ||
                    address.is_default === true ||
                    address.default === 1 ||
                    address.is_primary === 1 ||
                    addressData[0]?.address_id === address.address_id
                );

        const initialAddr = address.address_name || '';
        setMapSearchText(initialAddr);

        setFormData({
            address: initialAddr,
            province: address.province || '',
            city: address.city || '',
            district: address.district || '',
            village: address.village || '',
            phone: address.phone || '',
            latitude: parseFloat(address.latitude) || -7.3106144,
            longitude: parseFloat(address.longitude) || 112.6841525,
            isDefault: currentIsDefault,
        });

        try {
            // Load detail from backend
            const res = await axios({
                method: 'GET',
                url: `${process.env.REACT_APP_HOST_API}/address/get/${address.address_id}`,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = res.data?.response;
            if (data) {
                const provVal = data.province || address.province || '';
                const cityVal = data.city || address.city || '';
                const distVal = data.district || address.district || '';
                const villVal = data.village || address.village || '';
                const addrVal = data.address || address.address_name || '';

                setMapSearchText(addrVal);
                setFormData({
                    address: addrVal,
                    province: provVal,
                    city: cityVal,
                    district: distVal,
                    village: villVal,
                    phone: data.phone || address.phone || '',
                    latitude: parseFloat(data.latitude) || parseFloat(address.latitude) || -7.3106144,
                    longitude: parseFloat(data.longitude) || parseFloat(address.longitude) || 112.6841525,
                    isDefault: currentIsDefault,
                });

                if (provVal) {
                    if (locationCache.current.cities[provVal]) {
                        setCities(locationCache.current.cities[provVal]);
                    } else {
                        const cRes = await axios.get(
                            `${process.env.REACT_APP_HOST_API}/city/listing?province_id=${provVal}`
                        );
                        const cData = cRes.data?.response || [];
                        locationCache.current.cities[provVal] = cData;
                        setCities(cData);
                    }
                }
                if (cityVal) {
                    if (locationCache.current.districts[cityVal]) {
                        setDistricts(locationCache.current.districts[cityVal]);
                    } else {
                        const dRes = await axios.get(
                            `${process.env.REACT_APP_HOST_API}/district/listing?city_id=${cityVal}`
                        );
                        const dData = dRes.data?.response || [];
                        locationCache.current.districts[cityVal] = dData;
                        setDistricts(dData);
                    }
                }
                if (distVal) {
                    if (locationCache.current.villages[distVal]) {
                        setVillages(locationCache.current.villages[distVal]);
                    } else {
                        const vRes = await axios.get(
                            `${process.env.REACT_APP_HOST_API}/village/listing?district_id=${distVal}`
                        );
                        const vData = vRes.data?.response || [];
                        locationCache.current.villages[distVal] = vData;
                        setVillages(vData);
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching single address detail:', err);
        } finally {
            setModalLoading(false);
        }
    };

    // Map Click Handler in Modal
    const handleMapClick = useCallback(async (e) => {
        if (!e || !e.lat || !e.lng) return;
        const newLat = e.lat;
        const newLng = e.lng;

        setFormData((prev) => ({
            ...prev,
            latitude: newLat,
            longitude: newLng,
        }));

        try {
            const data = await getAddressMap(newLat, newLng);
            const addressText =
                data?.formatted_address ||
                data?.display_name ||
                (data?.results && data.results[0]?.formatted_address);
            if (addressText) {
                setMapSearchText(addressText);
                setFormData((prev) => ({
                    ...prev,
                    address: addressText,
                }));
            }
        } catch (error) {
            console.error('Error reverse geocoding map click:', error);
        }
    }, []);

    const handleSetLat = useCallback((val) => {
        setFormData((prev) => (prev.latitude === val ? prev : { ...prev, latitude: val }));
    }, []);

    const handleSetLng = useCallback((val) => {
        setFormData((prev) => (prev.longitude === val ? prev : { ...prev, longitude: val }));
    }, []);

    // Save Address Handler
    const handleSaveAddress = async (e) => {
        if (e) e.preventDefault();
        if (!formData.address || !formData.address.trim()) {
            toast.warn('Mohon masukkan alamat lengkap');
            return;
        }
        if (!formData.province) {
            toast.warn('Mohon pilih provinsi');
            return;
        }
        if (!formData.city) {
            toast.warn('Mohon pilih kota / kabupaten');
            return;
        }
        if (!formData.phone || !formData.phone.trim()) {
            toast.warn('Mohon masukkan nomor telepon / WhatsApp');
            return;
        }

        setSaving(true);
        dispatch({ type: 'UPDATE_ACTION', payload: true });

        try {
            const payload = {
                latitude: formData.latitude,
                longitude: formData.longitude,
                address: formData.address,
                city: formData.city,
                province: formData.province,
                district: formData.district || '',
                village: formData.village || '',
                phone: formData.phone,
                active_flag: 1,
            };

            let savedId = null;

            if (modalMode === 'edit' && editAddressId) {
                // Update Address via POST /address/update/{id}
                await axios({
                    method: 'POST',
                    url: `${process.env.REACT_APP_HOST_API}/address/update/${editAddressId}`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    data: payload,
                });
                savedId = editAddressId;
                toast.success('Alamat berhasil diperbarui');
            } else {
                // Add Address:
                // Count existing addresses to determine endpoint: pertama, kedua, ketiga
                let endpoint = '/address/pertama';
                if (addressData.length === 1) {
                    endpoint = '/address/kedua';
                } else if (addressData.length >= 2) {
                    endpoint = '/address/ketiga';
                }

                const res = await axios({
                    method: 'POST',
                    url: `${process.env.REACT_APP_HOST_API}${endpoint}`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    data: payload,
                });

                savedId = res.data?.response?.address_id || res.data?.response?.id;
                toast.success('Alamat berhasil ditambahkan');
            }

            // If user checked "Jadikan Alamat Utama"
            if (formData.isDefault && savedId) {
                try {
                    try {
                        await axios({
                            method: 'POST',
                            url: `${process.env.REACT_APP_HOST_API}/address/default/${savedId}`,
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                            data: { address_id: savedId, id: savedId },
                        });
                    } catch (dErr) {
                        if (dErr.response?.status === 404) {
                            await axios({
                                method: 'POST',
                                url: `${process.env.REACT_APP_HOST_API}/default/${savedId}`,
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'application/json',
                                },
                                data: { address_id: savedId, id: savedId },
                            });
                        }
                    }
                    setDefaultAddressId(savedId);
                    localStorage.setItem('DEFAULT_ADDRESS_ID', String(savedId));
                } catch (defErr) {
                    console.warn('Set default after save error:', defErr);
                }
            }

            setIsModalOpen(false);
            await fetchAddresses();
        } catch (error) {
            console.error('Save Address Error:', error);
            toast.error(error.response?.data?.message || 'Gagal menyimpan alamat');
        } finally {
            setSaving(false);
            dispatch({ type: 'UPDATE_ACTION', payload: false });
        }
    };

    // Keep primary / default address at the top of the addresses list
    const displayAddresses = useMemo(() => {
        if (!Array.isArray(addressData) || addressData.length === 0) return [];
        return [...addressData].sort((a, b) => {
            const aId = a.address_id ?? a.id;
            const bId = b.address_id ?? b.id;
            const aIsDef = defaultAddressId != null
                ? String(aId) === String(defaultAddressId)
                : (a.is_default === 0 || a.is_default === '0' || a.is_default === 1);
            const bIsDef = defaultAddressId != null
                ? String(bId) === String(defaultAddressId)
                : (b.is_default === 0 || b.is_default === '0' || b.is_default === 1);
            if (aIsDef && !bIsDef) return -1;
            if (!aIsDef && bIsDef) return 1;
            return 0;
        });
    }, [addressData, defaultAddressId]);

    return (
        <div className="addresses-page-wrapper">
            <Helmet>
                <title>{`Alamat Pengiriman | ${theme.name}`}</title>
            </Helmet>

            {/* Header Banner */}
            <div className="addresses-header-card">
                <div className="addresses-header__content">
                    <div className="addresses-header__icon-box">
                        <i className="fas fa-map-marked-alt" />
                    </div>
                    <div className="addresses-header__text">
                        <h3>Daftar Alamat Pengiriman</h3>
                        <p>Kelola alamat pengiriman Anda</p>
                    </div>
                </div>

                <div className="addresses-header__actions">
                    <button
                        type="button"
                        className="btn-add-address"
                        onClick={openAddModal}
                        disabled={addressData.length >= 3}
                        title={addressData.length >= 3 ? 'Maksimal 3 alamat tersimpan' : 'Tambah Alamat Baru'}
                    >
                        <i className="fas fa-plus" />
                        <span>Tambah Alamat</span>
                    </button>
                </div>
            </div>

            {/* Address List */}
            {loading ? (
                <div className="addresses-grid" style={{ pointerEvents: 'none' }}>
                    {[1, 2].map((i) => (
                        <div key={i} className="address-card address-card--skeleton" style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '22px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <span className="skeleton-bone" style={{ width: 130, height: 22, borderRadius: 5 }} />
                                <span className="skeleton-bone skeleton-bone--pill" style={{ width: 75, height: 20 }} />
                            </div>
                            <span className="skeleton-bone" style={{ width: 150, height: 18, borderRadius: 4, marginBottom: '8px' }} />
                            <span className="skeleton-bone" style={{ width: 120, height: 14, borderRadius: 4, marginBottom: '14px' }} />
                            <span className="skeleton-bone" style={{ width: '90%', height: 14, borderRadius: 4, marginBottom: '6px' }} />
                            <span className="skeleton-bone" style={{ width: '65%', height: 14, borderRadius: 4, marginBottom: '20px' }} />
                            <div style={{ display: 'flex', gap: '10px', paddingTop: '14px', borderTop: '1px solid #edf2f7' }}>
                                <span className="skeleton-bone" style={{ width: 90, height: 34, borderRadius: 8 }} />
                                <span className="skeleton-bone" style={{ width: 80, height: 34, borderRadius: 8 }} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : displayAddresses.length > 0 ? (
                <div className="addresses-grid">
                    {displayAddresses.map((address, idx) => {
                        const aId = address.address_id ?? address.id;
                        const isDefault =
                            defaultAddressId != null
                                ? String(aId) === String(defaultAddressId)
                                : Boolean(
                                    address.is_default === 0 ||
                                    address.is_default === '0' ||
                                    address.is_default === 1 ||
                                    address.is_default === true ||
                                    address.default === 1 ||
                                    address.is_primary === 1 ||
                                    idx === 0
                                );

                        const cardTitle = isDefault
                            ? 'Alamat Utama'
                            : (address.recipient_name || (displayAddresses.length > 2 ? `Alamat Tambahan ${idx}` : 'Alamat Tambahan'));

                        const fullAddressString = [
                            address.address_name,
                            address.city_name,
                            address.province_name,
                            address.postal_code,
                        ]
                            .filter(Boolean)
                            .join(', ');

                        return (
                            <div
                                key={aId || idx}
                                className={`address-item-card ${isDefault ? 'is-default' : ''}`}
                            >
                                <div className="address-item-card__header">
                                    <div className="address-item-card__title-group">
                                        <div className={`address-item-card__icon ${isDefault ? 'icon--default' : ''}`}>
                                            <i className="fas fa-map-marker-alt" />
                                        </div>
                                        <h5 className="address-item-card__recipient">
                                            {cardTitle}
                                        </h5>
                                    </div>
                                    {isDefault && (
                                        <span className="address-badge-utama">
                                            <i className="fas fa-check-circle" /> UTAMA
                                        </span>
                                    )}
                                </div>

                                <div className="address-item-card__body">
                                    <div className="address-item-card__text-box">
                                        <i className="fas fa-location-arrow pin-icon" />
                                        <div className="address-string">
                                            {fullAddressString || 'Belum ada detail alamat'}
                                        </div>
                                    </div>

                                    {address.phone && (
                                        <div className="address-item-card__contact-row">
                                            <span className="address-item-card__contact-chip">
                                                <i className="fas fa-phone-alt" />
                                                <span>{address.phone}</span>
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="address-item-card__footer">
                                    {!isDefault && (
                                        <button
                                            type="button"
                                            className="btn-address-action btn-address-action--default"
                                            onClick={() => handleSetDefault(address.address_id)}
                                            disabled={String(settingDefaultId) === String(address.address_id)}
                                            title="Atur sebagai Alamat Utama"
                                        >
                                            {String(settingDefaultId) === String(address.address_id) ? (
                                                <i className="fas fa-spinner fa-spin" />
                                            ) : (
                                                <>
                                                    <i className="far fa-check-circle mr-1" />
                                                    <span>Jadikan Utama</span>
                                                </>
                                            )}
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="btn-address-action btn-address-action--edit"
                                        onClick={() => openEditModal(address)}
                                    >
                                        <i className="fas fa-edit mr-1" />
                                        <span>Ubah Alamat</span>
                                    </button>

                                    {addressData.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn-address-action btn-address-action--delete"
                                            onClick={() => setDeleteModal({ isOpen: true, address, loading: false })}
                                            title="Hapus Alamat"
                                        >
                                            <i className="fas fa-trash-alt mr-1" />
                                            <span>Hapus</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <EmptyState
                    icon="map-marked-alt"
                    title="Belum ada alamat"
                    text="Tambahkan alamat untuk memudahkan checkout."
                    actionLabel="Tambah Alamat"
                    actionIcon="plus"
                    onAction={openAddModal}
                />
            )}

            {/* Modal Tambah / Ubah Alamat via React Portal (Always on Top of entire screen) */}
            {isModalOpen &&
                ReactDOM.createPortal(
                    <div
                        className="address-modal-overlay"
                        onClick={() => !saving && setIsModalOpen(false)}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div
                            className="address-modal-dialog"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="address-modal-header">
                                <div className="modal-title-group">
                                    <div className="modal-title-icon">
                                        <i className={modalMode === 'add' ? 'fas fa-plus' : 'fas fa-edit'} />
                                    </div>
                                    <div>
                                        <h4 className="modal-title-text">
                                            {modalMode === 'add'
                                                ? 'Tambah Alamat Pengiriman'
                                                : 'Ubah Alamat Pengiriman'}
                                        </h4>
                                        <p className="modal-title-sub">
                                            {modalMode === 'add'
                                                ? 'Tentukan lokasi dan data lengkap alamat pengiriman baru Anda'
                                                : 'Perbarui data detail dan titik peta lokasi pengiriman Anda'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="modal-close-btn"
                                    onClick={() => !saving && setIsModalOpen(false)}
                                    disabled={saving}
                                    title="Tutup (Esc)"
                                >
                                    <i className="fas fa-times" />
                                </button>
                            </div>

                            <form onSubmit={handleSaveAddress} className="address-modal-form">
                                <div className="address-modal-body">
                                    {modalLoading ? (
                                        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                                            <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#0064d2' }} />
                                            <p className="mt-3 text-muted">Memuat data alamat...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Optimized Memoized Map Section */}
                                            <AddressMapSection
                                                latitude={formData.latitude}
                                                longitude={formData.longitude}
                                                mapSearchText={mapSearchText}
                                                onMapClick={handleMapClick}
                                                onSetLat={handleSetLat}
                                                onSetLng={handleSetLng}
                                            />

                                            {/* Detail Form Section */}
                                            <div className="modal-section-card">
                                                <div className="modal-section-title">
                                                    <i className="fas fa-clipboard-list" />
                                                    <span>Detail Alamat Lengkap</span>
                                                </div>

                                                <div className="modal-form-grid">
                                                    {/* Alamat Lengkap */}
                                                    <div className="modal-form-group col-full">
                                                        <label className="modal-form-label">
                                                            Alamat Lengkap (Jalan, No. Rumah, RT/RW, Patokan) <span className="text-danger">*</span>
                                                        </label>
                                                        <textarea
                                                            className="modal-form-input"
                                                            rows="3"
                                                            placeholder="Contoh: Jl. Raya Lidah Kulon No. 45, RT 02 / RW 05 (Pagar hitam samping minimarket)"
                                                            value={formData.address}
                                                            onChange={(e) =>
                                                                setFormData((prev) => ({ ...prev, address: e.target.value }))
                                                            }
                                                            required
                                                        />
                                                    </div>

                                                    {/* Provinsi */}
                                                    <div className="modal-form-group col-half">
                                                        <label className="modal-form-label">
                                                            Provinsi <span className="text-danger">*</span>
                                                        </label>
                                                        <select
                                                            className="modal-form-select"
                                                            value={formData.province}
                                                            onChange={(e) => handleProvinceChange(e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Pilih Provinsi</option>
                                                            {provinces.map((p) => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Kota / Kabupaten */}
                                                    <div className="modal-form-group col-half">
                                                        <label className="modal-form-label">
                                                            Kota / Kabupaten <span className="text-danger">*</span>
                                                        </label>
                                                        <select
                                                            className="modal-form-select"
                                                            value={formData.city}
                                                            onChange={(e) => handleCityChange(e.target.value)}
                                                            disabled={!formData.province || cities.length === 0}
                                                            required
                                                        >
                                                            <option value="">
                                                                {!formData.province ? 'Pilih Provinsi Dulu' : 'Pilih Kota / Kabupaten'}
                                                            </option>
                                                            {cities.map((c) => (
                                                                <option key={c.id} value={c.id}>
                                                                    {c.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Kecamatan */}
                                                    <div className="modal-form-group col-half">
                                                        <label className="modal-form-label">Kecamatan</label>
                                                        <select
                                                            className="modal-form-select"
                                                            value={formData.district}
                                                            onChange={(e) => handleDistrictChange(e.target.value)}
                                                            disabled={!formData.city || districts.length === 0}
                                                        >
                                                            <option value="">
                                                                {!formData.city ? 'Pilih Kota Dulu' : 'Pilih Kecamatan'}
                                                            </option>
                                                            {districts.map((d) => (
                                                                <option key={d.id} value={d.id}>
                                                                    {d.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Kelurahan / Desa */}
                                                    <div className="modal-form-group col-half">
                                                        <label className="modal-form-label">Kelurahan / Desa</label>
                                                        <select
                                                            className="modal-form-select"
                                                            value={formData.village}
                                                            onChange={(e) =>
                                                                setFormData((prev) => ({ ...prev, village: e.target.value }))
                                                            }
                                                            disabled={!formData.district || villages.length === 0}
                                                        >
                                                            <option value="">
                                                                {!formData.district ? 'Pilih Kecamatan Dulu' : 'Pilih Kelurahan / Desa'}
                                                            </option>
                                                            {villages.map((v) => (
                                                                <option key={v.id} value={v.id}>
                                                                    {v.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Nomor Telepon */}
                                                    <div className="modal-form-group col-full">
                                                        <label className="modal-form-label">
                                                            Nomor Telepon / WhatsApp Penerima <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="modal-input-with-icon">
                                                            <i className="fas fa-phone-alt" />
                                                            <input
                                                                type="text"
                                                                className="modal-form-input input-phone"
                                                                placeholder="Contoh: 081234567890"
                                                                value={formData.phone}
                                                                onChange={(e) =>
                                                                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                                                                }
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Checkbox Jadikan Alamat Utama */}
                                                    <div className="modal-form-group col-full mt-2">
                                                        <label className="modal-checkbox-row">
                                                            <input
                                                                type="checkbox"
                                                                className="modal-checkbox-input"
                                                                checked={formData.isDefault}
                                                                onChange={(e) =>
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        isDefault: e.target.checked,
                                                                    }))
                                                                }
                                                            />
                                                            <span className="modal-checkbox-custom" />
                                                            <span className="modal-checkbox-text">
                                                                <strong>Jadikan sebagai Alamat Utama</strong>
                                                                <span className="modal-checkbox-desc">
                                                                    Alamat ini akan otomatis dipilih sebagai tujuan pengiriman saat checkout.
                                                                </span>
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="address-modal-footer">
                                    <button
                                        type="button"
                                        className="btn-modal-cancel"
                                        onClick={() => setIsModalOpen(false)}
                                        disabled={saving}
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-modal-save"
                                        disabled={saving || modalLoading}
                                    >
                                        {saving ? (
                                            <i className="fas fa-spinner fa-spin" />
                                        ) : (
                                            <>
                                                <i className="fas fa-check mr-1" />
                                                <span>
                                                    {modalMode === 'add' ? 'Simpan Alamat Baru' : 'Simpan Perubahan'}
                                                </span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>,
                    document.body
                )}

            {/* Modal Konfirmasi Hapus via React Portal */}
            {deleteModal.isOpen &&
                ReactDOM.createPortal(
                    <div
                        className="address-modal-overlay"
                        onClick={() =>
                            !deleteModal.loading && setDeleteModal({ isOpen: false, address: null, loading: false })
                        }
                        role="dialog"
                        aria-modal="true"
                    >
                        <div
                            className="delete-confirm-dialog"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="delete-confirm-icon">
                                <i className="fas fa-exclamation-triangle" />
                            </div>
                            <h4 className="delete-confirm-title">Hapus Alamat Pengiriman?</h4>
                            <p className="delete-confirm-desc">
                                Apakah Anda yakin ingin menghapus alamat ini? Alamat yang dihapus tidak dapat dipulihkan.
                            </p>
                            {deleteModal.address && (
                                <div className="delete-confirm-address-preview">
                                    <i className="fas fa-map-marker-alt mr-2" />
                                    <span>
                                        {[deleteModal.address.address_name, deleteModal.address.city_name]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </span>
                                </div>
                            )}
                            <div className="delete-confirm-actions">
                                <button
                                    type="button"
                                    className="btn-modal-cancel"
                                    onClick={() => setDeleteModal({ isOpen: false, address: null, loading: false })}
                                    disabled={deleteModal.loading}
                                >
                                    Batal
                                </button>
                                <button
                                    type="button"
                                    className="btn-modal-danger"
                                    onClick={confirmDeleteAddress}
                                    disabled={deleteModal.loading}
                                >
                                    {deleteModal.loading ? (
                                        <i className="fas fa-spinner fa-spin" />
                                    ) : (
                                        <>
                                            <i className="fas fa-trash-alt mr-1" />
                                            <span>Ya, Hapus Alamat</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default AccountPageAddresses;